// src/screens/DashboardScreen/DashboardScreen.jsx
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useUserStats } from '../../hooks/useUserStats';
import { useAuth } from '../../context/AuthContext';
import { useGame } from '../../context/GameContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { LoadingSpinner } from '../../components/common/LoadingSpinner/LoadingSpinner';
import { useScaleWrapperBackground, SCALE_WRAPPER_DEFAULT_BG } from '../../hooks/useScaleWrapperBackground';
import {
  buildBlastConfig,
  buildFixedHandsConfig,
  formatBlastTitle,
  getDefaultStackForPosition,
} from '../BlastConfigScreen/blastRangeRules';
import './dashboard.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Returns array of 10 circle types ('filled' | 'half' | 'empty') for a given avg score
function getAvgScoreCircles(avgScore) {
  const circles = [];
  const whole = Math.floor(avgScore);
  const hasHalf = avgScore - whole >= 0.5;

  for (let i = 0; i < 10; i++) {
    if (i < whole) circles.push('filled');
    else if (i === whole && hasHalf) circles.push('half');
    else circles.push('empty');
  }
  return circles;
}

// Returns last 6 quiz accuracies for the trend bar chart
function getTrendBars(historyWindow) {
  const last6 = [...historyWindow].slice(-6);
  while (last6.length < 6) last6.unshift({ accuracy: 0 });
  return last6.map(q => q.accuracy ?? 0);
}

// Returns array of 10 booleans (true=correct) for the X/O grid
function getLastDecisions(lastDecisions) {
  const decisions = [...(lastDecisions || [])].slice(-10);
  while (decisions.length < 10) decisions.unshift(false);
  return decisions;
}

// Scale bar height proportionally; max bar = 45px, min = 4px
function barHeight(accuracy) {
  return Math.max(Math.round((accuracy / 100) * 45), 4);
}

// ── Component ─────────────────────────────────────────────────────────────────

// Row layout constants for the expand/collapse animation (see dash-row /
// dash-card / dash-card-inner in dashboard.css). Row is 343px wide with an
// 8px gap between the two cards; an expanded card takes the full row minus
// that gap, its sibling genuinely shrinks all the way to 0 — the shell's
// own border-radius:40px is never touched/scaled in script, so the
// shrinking shell resolves into a clean capsule shape on its own as it
// narrows, rather than something that looks distorted.
const CARD_ROW_WIDTH = 343;
const CARD_GAP = 8;
const CARD_COLLAPSED_WIDTH = 0;
const CARD_EXPANDED_WIDTH = CARD_ROW_WIDTH - CARD_GAP;
// A more pronounced ease-in-out than the default curve, plus a touch more
// duration so the curve actually reads at this speed (250ms was too fast
// to perceive any easing at all).
const CARD_TWEEN = { duration: 0.32, ease: [0.65, 0, 0.35, 1] };

// Recommended-card title format prefix ("HU | ..." / "3way | ...").
// formatBlastTitle() deliberately omits this (see its docstring — the
// format normally belongs in a screen's page header), but the dashboard's
// Recommended card has no page header of its own, so it's prefixed here
// rather than changing the shared title formatter used by other screens.
const RECOMMENDED_FORMAT_LABEL = { hu: 'HU', '3way': '3way' };

const DashboardScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userStats, loading, error: statsError, refetch: refetchStats } = useUserStats(user?.uid);
  const { startNewGame } = useGame();

  const userInitial = user?.displayName
    ? user.displayName.charAt(0).toUpperCase()
    : user?.email?.charAt(0).toUpperCase() ?? '?';

  const handleAvatarPress = () => {
    // Pass what we already know (isEmptyState, defined below) along with
    // the navigation so AccountScreen can decide whether to show its
    // quiz-data-dependent sections on its very first render, instead of
    // defaulting to "has data" and then snapping a section away once its
    // own stats fetch resolves a moment later. See the /account link in
    // the empty-state JSX below for the other call site.
    navigate('/account', { state: { hasNoQuizData: isEmptyState } });
  };

  // Which stat card (if any) is expanded — only one of the four cards can
  // be open at a time, across both rows. Tapping the open card collapses
  // it back to null; tapping any other card (same row or the other row)
  // swaps directly to it, closing whatever was open first. There's no
  // dedicated Close button — tapping the open card again is how you close
  // it.
  const [expandedCard, setExpandedCard] = useState(null);
  const toggleCard = (card) => {
    setExpandedCard(prev => (prev === card ? null : card));
  };
  const TOP_ROW_CARDS = ['accuracy', 'trend'];
  const BOTTOM_ROW_CARDS = ['avgscore', 'decisions'];
  const topCardWidth = (card) => {
    if (expandedCard === card) return CARD_EXPANDED_WIDTH;
    if (TOP_ROW_CARDS.includes(expandedCard)) return CARD_COLLAPSED_WIDTH;
    return card === 'accuracy' ? 168 : 167; // baseline (nothing expanded in this row)
  };
  // Accuracy (row-leading) only ever needs the "live" content anchor — see
  // dash-card-inner in dashboard.css. Trend (row-trailing) is the one that
  // needs to switch: "masked" only while it's being passively squeezed to 0
  // by Accuracy opening, "live" every other moment (resting, opening
  // itself, closing itself) so its content rides along with its own moving
  // edge instead of sliding independently of the shrinking shell.
  const trendInnerMode = expandedCard === 'accuracy' ? 'masked' : 'live';

  const bottomCardWidth = (card) => {
    if (expandedCard === card) return CARD_EXPANDED_WIDTH;
    if (BOTTOM_ROW_CARDS.includes(expandedCard)) return CARD_COLLAPSED_WIDTH;
    return 167; // baseline — both cards are 167px, unlike the top row's 168/167 split
  };
  // Same reasoning as trendInnerMode above — Decisions (row-trailing) is
  // the one that needs to switch between "masked" (passive) and "live".
  const decisionsInnerMode = expandedCard === 'avgscore' ? 'masked' : 'live';

  const overall = userStats?.overall || {};
  // Only true once we've actually confirmed it via a successful fetch —
  // while userStats is still loading, `overall` is `{}` and this would
  // otherwise read as "empty" by default, flashing the letterbox brown
  // (below) before flipping back to cream if the account turns out to have
  // quizzes after all. Same reasoning for a failed fetch (statsError): a
  // retried-and-still-failed /api/user-stats call left userStats null
  // exactly like a genuinely brand-new account, which previously showed
  // "Let's Create Your First Quiz" to a real user whose data just didn't
  // load — now it falls through to the loading placeholder below instead
  // of asserting something we don't actually know.
  const isEmptyState = !loading && !statsError && (overall.total_quizzes ?? 0) === 0;

  // ScaleWrapper zooms a fixed 375x812 canvas to fit the real device
  // viewport, which can leave a sliver of letterbox background showing on
  // the sides or bottom on devices whose aspect ratio isn't exactly
  // 375:812 (see ScaleWrapper.css). That sliver is invisible against the
  // app's default cream, but glaring against the empty state's brown — so
  // claim it here for as long as this screen is in its empty state, and
  // hand it back on unmount/exit.
  useScaleWrapperBackground(isEmptyState ? '#864F38' : SCALE_WRAPPER_DEFAULT_BG);

  const accuracy = overall.accuracy ?? 0;
  const avgScore = overall.avg_score ?? 0;
  const trend = overall.trend ?? 0;
  const totalCorrect = overall.correct_decisions ?? 0;
  const totalDecisions = overall.total_decisions ?? 0;
  const totalQuizzes = overall.total_quizzes ?? 0;
  const historyWindow = userStats?.quiz_history_window || [];
  const lastDecisions = userStats?.last_decisions || [];

  // Trend needs 3 quizzes minimum before it's a real signal (see
  // DASHBOARD_SPEC.md Section 4 and server.js's own `trend = 0` gate below
  // 3 quizzes). Below that, the card shows a countdown message instead of
  // a bogus 0.0%/flat trend, and the bars are a decorative flat row rather
  // than real (partially-populated) history data.
  const ENOUGH_QUIZZES_FOR_TREND = 3;
  const trendDataReady = totalQuizzes >= ENOUGH_QUIZZES_FOR_TREND;
  const quizzesUntilTrend = ENOUGH_QUIZZES_FOR_TREND - totalQuizzes;

  // suggested_drill is null unless a specific scenario has >=15 decisions
  // (see server.js). No random/placeholder fallback — if the data doesn't
  // support a real recommendation, this stays null and the card shows the
  // quiz-count progress state instead (below).
  const suggestedDrill = userStats?.suggested_drill ?? null;
  const scenario = suggestedDrill?.dimension === 'scenario' ? suggestedDrill.scenario : null;

  // Progress toward the closest-to-qualifying scenario (15 decisions in one
  // exact scenario — see server.js), not a flat "20 quizzes played" count.
  // Total sessions played and "have a qualifying scenario" are different
  // axes: someone can play 30 varied quizzes and still have no single
  // scenario near the threshold, so this reflects the real distance.
  const quizzesRemaining = userStats?.quizzes_to_go ?? 2;

  // Recommended card title — built only from a real, specific scenario via
  // formatBlastTitle(), the same function used for quiz page titles
  // elsewhere in the app. Null when there's no qualifying scenario.
  const recommendedTitle = useMemo(() => {
    if (!scenario) return null;
    const stackForLabel = suggestedDrill.worst_stack
      ?? getDefaultStackForPosition(scenario.format, scenario.position);
    const baseTitle = formatBlastTitle({ ...scenario, stack: stackForLabel });
    const formatLabel = RECOMMENDED_FORMAT_LABEL[scenario.format];
    return formatLabel ? `${formatLabel} | ${baseTitle}` : baseTitle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.format, scenario?.position, scenario?.primaryAction,
      scenario?.secondaryAction, scenario?.tertiaryAction, suggestedDrill?.worst_stack]);

  // ── Recommended drill handlers ────────────────────────────────────────────
  // Call the exact same config builders the manual config screen uses
  // (buildBlastConfig / buildFixedHandsConfig), so pressing these buttons
  // reproduces the flagged scenario precisely. Only reachable when a real
  // scenario exists — the buttons aren't rendered otherwise.
  const handleFixedBlinds = () => {
    if (!scenario) return;
    const stack = suggestedDrill.worst_stack
      ?? getDefaultStackForPosition(scenario.format, scenario.position);
    const config = buildBlastConfig(
      scenario.format, scenario.position, stack,
      scenario.primaryAction, scenario.secondaryAction, scenario.tertiaryAction
    );
    navigate('/blast-range', { state: { config } });
  };

  const handleMixedBlinds = () => {
    if (!scenario) return;
    const config = buildFixedHandsConfig(
      scenario.format, scenario.position,
      scenario.primaryAction, scenario.secondaryAction, scenario.tertiaryAction
    );
    startNewGame(config);
    setTimeout(() => navigate('/blast'), 100);
  };

  const circles = getAvgScoreCircles(avgScore);
  // Below the 3-quiz threshold the bars are purely decorative (per Figma —
  // a flat row, not a partial rendering of the 1-2 real quizzes on hand),
  // so don't feed real historyWindow data into them yet.
  const trendBars = trendDataReady ? getTrendBars(historyWindow) : [0, 0, 0, 0, 0, 0];
  const decisionSymbols = getLastDecisions(lastDecisions);
  // Only prefix a sign when there's an actual value to show — a flat 0.0%
  // (no change, or not enough data yet) renders as "0.0", not "+0.0".
  const trendDisplay = trend > 0 ? `+${trend.toFixed(1)}` : trend.toFixed(1);

  // Still resolving — userStats isn't known yet either way.
  if (loading) {
    return (
      <div className="dash-screen">
        <div className="dash-container">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Failed even after fetchWithRetry's own retries (see useUserStats) — a
  // real connection problem, not "this account has zero quizzes." Falling
  // through to the real dashboard below would render fabricated 0%/0.0
  // stats as if they were this account's actual numbers; falling through
  // to the empty state would tell a real user they have no quizzes. Neither
  // is true, so this gets its own state instead of reusing either one.
  //
  // TEMPORARY placeholder — no design pass has happened on this yet
  // (per explicit instruction, revisit later). Reuses only existing
  // pieces (LoadingSpinner, ButtonLarge) rather than any new custom UI.
  if (statsError) {
    return (
      <div className="dash-screen">
        <div className="dash-container dash-stats-error">
          <LoadingSpinner
            fullHeight={false}
            label="Having trouble connecting. Check your connection and try again."
          />
          <ButtonLarge
            buttonLargeClassName="button-large dash-stats-error-retry"
            text="Retry"
            onClick={refetchStats}
          />
        </div>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────
  // Shown on first login, and any time the user hasn't finished a single
  // quiz yet — including a quiz they started and then canceled out of.
  // Canceled attempts never reach /api/submit-quiz, so total_quizzes stays
  // 0 exactly like a brand-new account; no separate "canceled" tracking is
  // needed. Goes away permanently the moment total_quizzes > 0.
  if (isEmptyState) {
    return (
      <div className="dash-screen dash-screen--empty">
        <div className="dash-container dash-container--empty">
          <img src="/svgs/empty-state-bg.svg" alt="" className="dash-empty-bg" />

          <div className="dash-empty-header">
            <p className="dash-empty-heading">Let’s Create Your First Quiz</p>
            <img src="/svgs/empty-state-graphic.svg" alt="" className="dash-empty-graphic" />
          </div>

          <div className="dash-empty-copy">
            <p className="dash-empty-body">
              Every quiz helps build a clearer picture of your game. It all starts with your first quiz.
            </p>
            <div className="dash-empty-bullets-wrap">
              <p className="dash-empty-bullets-title">What you’ll see after your first quiz:</p>
              <ul className="dash-empty-bullets">
                <li>Overall Accuracy</li>
                <li>Average Score</li>
                <li>Decision History</li>
              </ul>
            </div>
          </div>

          <div className="dash-empty-cta-wrap">
            <ButtonLarge
              buttonLargeClassName="button-large dash-empty-cta-button"
              text="Create My First Quiz"
              onClick={() => navigate('/blast-config')}
            />
          </div>

          <button
            type="button"
            className="dash-empty-account-link"
            onClick={() => navigate('/account', { state: { hasNoQuizData: isEmptyState } })}
          >
            Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-screen">
      <div className="dash-container">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="dash-header">
          <span className="dash-header-title">Your Progress</span>
          <div className="dash-avatar" onClick={handleAvatarPress} role="button" style={{ cursor: 'pointer' }}>
            <img src="/svgs/user-avatar.svg" alt="" className="dash-avatar-img" />
            <span className="dash-avatar-initial">{userInitial}</span>
          </div>
        </div>

        {/* ── 2×2 Stat Cards ──────────────────────────────────────────────── */}
        <div className="dash-cards-grid">

          {/* Row 1 — Overall Accuracy / Latest Trend. Tap either card to
              expand it in place (see dash-card-inner in dashboard.css for
              how the "mask" effect works); tapping any other card — in
              this row or the other one — swaps directly to it, since only
              one card can be open at a time. */}
          <div className="dash-row">

            {/* Card 1 — Overall Accuracy (purple) */}
            <motion.div
              className="dash-card dash-card--purple"
              animate={{ width: topCardWidth('accuracy') }}
              transition={CARD_TWEEN}
              onClick={() => toggleCard('accuracy')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && toggleCard('accuracy')}
            >
              <div className="dash-card-inner dash-card-inner--live">
                <div className="dash-card-primary">
                  <div className="dash-card-top">
                    <p className="dash-card-label">Overall<br />Accuracy</p>
                    <div className="dash-card-value">
                      <span className="dash-card-num">{accuracy.toFixed(1)}</span>
                      <span className="dash-card-unit">%</span>
                    </div>
                  </div>
                  <div className="dash-card-graphic">
                    <img src="/svgs/accuracy-graphic.svg" alt="" className="dash-accuracy-graphic" />
                  </div>
                </div>
                <AnimatePresence>
                  {expandedCard === 'accuracy' && (
                    <motion.div
                      className="dash-card-explainer-group"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={CARD_TWEEN}
                    >
                      <div className="dash-card-divider" />
                      <div className="dash-card-explainer">
                        <p className="dash-card-explainer-text">
                          Shows how often<br />you choose the<br />correct action<br />across every quiz<br />you completed.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Card 2 — Latest Trend (orange) */}
            <motion.div
              className="dash-card dash-card--orange"
              animate={{ width: topCardWidth('trend') }}
              transition={CARD_TWEEN}
              onClick={() => toggleCard('trend')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && toggleCard('trend')}
            >
              <div className={`dash-card-inner dash-card-inner--${trendInnerMode}`}>
                <div className="dash-card-primary">
                  <div className="dash-card-top">
                    <p className="dash-card-label dash-card-label--dark">Latest<br />Trend</p>
                    {trendDataReady ? (
                      <div className="dash-card-value dash-card-value--dark">
                        <span className="dash-card-num">{trendDisplay}</span>
                        <span className="dash-card-unit">%</span>
                      </div>
                    ) : (
                      <p className="dash-trend-empty-msg">
                        We'll start showing trends
                        <br />
                        after {quizzesUntilTrend} more {quizzesUntilTrend === 1 ? 'quiz' : 'quizzes'}.
                      </p>
                    )}
                  </div>
                  <div className="dash-bars">
                    {trendBars.map((acc, i) => (
                      <div
                        key={i}
                        className={`dash-bar ${i >= 3 ? 'dash-bar--recent' : 'dash-bar--older'}`}
                        style={{ height: `${barHeight(acc)}px` }}
                      />
                    ))}
                  </div>
                </div>
                <AnimatePresence>
                  {expandedCard === 'trend' && (
                    <motion.div
                      className="dash-card-explainer-group"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={CARD_TWEEN}
                    >
                      <div className="dash-card-divider" />
                      <div className="dash-card-explainer">
                        <p className="dash-card-explainer-text dash-card-explainer-text--dark">
                          Tracks how your<br />performance has<br />changed over<br />your last three<br />quizzes.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

          </div>

          {/* Row 2 — Avg Score / Correct Decisions. Same mechanics as row 1,
              sharing the same single expandedCard state. */}
          <div className="dash-row">

            {/* Card 3 — Avg Score (yellow) */}
            <motion.div
              className="dash-card dash-card--yellow"
              animate={{ width: bottomCardWidth('avgscore') }}
              transition={CARD_TWEEN}
              onClick={() => toggleCard('avgscore')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && toggleCard('avgscore')}
            >
              <div className="dash-card-inner dash-card-inner--live">
                <div className="dash-card-primary">
                  <div className="dash-card-top">
                    <p className="dash-card-label dash-card-label--dark">Avg<br />Score</p>
                    <div className="dash-card-value dash-card-value--dark dash-card-value--decisions">
                      <span className="dash-card-num">{avgScore.toFixed(1)}</span>
                      <span className="dash-card-unit">/</span>
                      <span className="dash-card-unit">10</span>
                    </div>
                  </div>
                  <div className="dash-circles">
                    {circles.map((type, i) => (
                      <img
                        key={i}
                        src={`/svgs/avg-score-circle-${type}.svg`}
                        alt=""
                        className="dash-circle"
                      />
                    ))}
                  </div>
                </div>
                <AnimatePresence>
                  {expandedCard === 'avgscore' && (
                    <motion.div
                      className="dash-card-explainer-group"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={CARD_TWEEN}
                    >
                      <div className="dash-card-divider dash-card-divider--dark" />
                      <div className="dash-card-explainer">
                        <p className="dash-card-explainer-text dash-card-explainer-text--dark">
                          Shows your<br />average score<br />across all of your<br />completed<br />quizzes.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Card 4 — Correct Decisions (blue) */}
            <motion.div
              className="dash-card dash-card--blue"
              animate={{ width: bottomCardWidth('decisions') }}
              transition={CARD_TWEEN}
              onClick={() => toggleCard('decisions')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && toggleCard('decisions')}
            >
              <div className={`dash-card-inner dash-card-inner--${decisionsInnerMode}`}>
                <div className="dash-card-primary">
                  <div className="dash-card-top">
                    <p className="dash-card-label">Correct<br />Decisions</p>
                    <div className="dash-card-value dash-card-value--decisions">
                      <span className="dash-card-num">{totalCorrect}</span>
                      <span className="dash-card-unit">/</span>
                      <span className="dash-card-unit">{totalDecisions}</span>
                    </div>
                  </div>
                  <div className="dash-symbols">
                    {decisionSymbols.map((correct, i) => (
                      <img
                        key={i}
                        src={correct
                          ? '/svgs/total-decisions-o (Stroke).svg'
                          : '/svgs/total-decisions-x.svg'}
                        alt={correct ? 'correct' : 'incorrect'}
                        className="dash-symbol"
                      />
                    ))}
                  </div>
                </div>
                <AnimatePresence>
                  {expandedCard === 'decisions' && (
                    <motion.div
                      className="dash-card-explainer-group"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={CARD_TWEEN}
                    >
                      <div className="dash-card-divider" />
                      <div className="dash-card-explainer">
                        <p className="dash-card-explainer-text">
                          Shows how many<br />correct decisions<br />you've made out<br />of every decision<br />answered.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

          </div>

        </div>

        {/* ── Quizzes Section — pinned to bottom ──────────────────────────── */}
        <div className="dash-quizzes">

          <div className="dash-section-heading">
            <span className="dash-section-title">Keep Improving</span>
          </div>

          {/* Create Your Own banner */}
          <div
            className="dash-create-banner"
            onClick={() => navigate('/blast-config')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate('/blast-config')}
          >
            <div className="dash-create-bg" />
            <div className="dash-create-inner">
              <div className="dash-create-text">
                <p className="dash-create-subtitle">Train Another Spot</p>
                <p className="dash-create-title">Create a New Quiz</p>
              </div>
              <img src="/svgs/blast-graphic.svg" alt="" className="dash-create-graphic" />
            </div>
          </div>

          {/* Recommended card — only shows a drill once a specific scenario
              genuinely qualifies (see suggested_drill in /api/user-stats).
              Otherwise shows quiz-count progress, never a fabricated quiz. */}
          <div className="dash-recommended">
            <img src="/svgs/recommended-graphic.svg" alt="" className="dash-rec-graphic" />
            {scenario ? (
              <div className="dash-rec-content">
                <div className="dash-rec-text">
                  <p className="dash-rec-label">Recommended</p>
                  <p className="dash-rec-title">{recommendedTitle}</p>
                </div>
                <div className="dash-rec-buttons">
                  <button className="dash-rec-btn" onClick={handleFixedBlinds}>
                    Fixed Blinds
                  </button>
                  <button className="dash-rec-btn" onClick={handleMixedBlinds}>
                    Mixed Blinds
                  </button>
                </div>
              </div>
            ) : (
              <div className="dash-rec-content dash-rec-content--empty">
                <p className="dash-rec-empty-title">
                  {quizzesRemaining} {quizzesRemaining === 1 ? 'Quiz' : 'Quizzes'} to go!
                </p>
                <p className="dash-rec-empty-sub">
                  Then we'll build custom quizzes targeting your biggest leaks.
                </p>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default DashboardScreen;
